import {
	Component, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, SimpleChange,
	EventEmitter, ChangeDetectorRef, ViewChild
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { ApiKeysService } from '../../services/apikeys.service';
import { MessageService } from 'base';
import { List } from 'DiscoveryEngine.TS/Utils';
import { UserDto, ApiKeyDto, ApiKeyTransferResultDto } from 'UvExplorerServer.Dtos.TS/Auth';
import { LicensingService, IdentityService } from 'identity';
import { DateService } from 'base';


@Component({
	selector: 'apikey-list',
	templateUrl: './apikey-list.component.html',
	styleUrls: ['./apikey-list.component.scss']
})
export class ApiKeyListComponent implements OnInit, OnDestroy, OnChanges {

	public entries: any[];
	public apiKeyDialogVisible: boolean;
	public apiKeyDialogTarget: ApiKeyDto;
	public apiKeyDialogCreate: boolean;
	public displayDeleteApiKeyConfirm: boolean;
	private apiKeyToDelete: ApiKeyDto;
	public displayUserSelectorDialog: boolean;

	private loginSub: Subscription;
	private logoutSub: Subscription;

	constructor(private identityService: IdentityService,
		private apiKeysService: ApiKeysService,
		private licensingService: LicensingService,
		private messageService: MessageService,
		private dateService: DateService) {

		this.entries = [];
		this.apiKeyDialogVisible = false;
		this.apiKeyDialogTarget = null;
		this.apiKeyDialogCreate = false;
		this.apiKeyToDelete = null;
		this.displayUserSelectorDialog = false;

		this.loginSub = identityService.onLogin.subscribe((user: UserDto) => {
			this.refreshApiKeys();
		});

		this.logoutSub = identityService.onLogout.subscribe(() => {
			this.entries = [];
		});
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public ngOnInit(): void {
		this.refreshApiKeys();
	}

	public ngOnDestroy(): void {
		if (this.loginSub) {
			this.loginSub.unsubscribe();
			this.loginSub = undefined;
		}
		if (this.logoutSub) {
			this.logoutSub.unsubscribe();
			this.logoutSub = undefined;
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	public get isAdmin(): boolean {
		return this.identityService.isAdmin;
	}

	public refreshApiKeys(): void {
		if (this.identityService.isLoggedIn) {
			this.getApiKeys();
		}
	}

	public createApiKey(): void {
		this.apiKeyDialogCreate = true;
		this.apiKeyDialogTarget = new ApiKeyDto();
		this.apiKeyDialogVisible = true;
	}

	public editApiKey(apiKey: ApiKeyDto): void {
		this.apiKeyDialogCreate = false;
		this.apiKeyDialogTarget = apiKey;
		this.apiKeyDialogVisible = true;
	}

	public deleteApiKey(apiKey: ApiKeyDto): void {
		this.apiKeyToDelete = apiKey;
		this.displayDeleteApiKeyConfirm = true;
	}

	private get apiKeys(): ApiKeyDto[] {
		let apiKeys: ApiKeyDto[] = [];
		if (this.entries) {
			apiKeys = this.entries.map(e => e.tag);
		}
		return apiKeys;
	}

	private getApiKeys(): void {
		this.apiKeysService.getApiKeys().subscribe(
			(result: List<ApiKeyDto>) => {
				this.rebuildEntries(result.ToArray());
			},
			(error) => {
				this.messageService.displayErrorMessage("Could not retrieve API key information");
				this.rebuildEntries([]);
			});
	}

	private rebuildEntries(apiKeys: ApiKeyDto[]): void {
		this.entries = [];

		// Initialize entries
		apiKeys.forEach((apiKey: ApiKeyDto) => {
			this.addEntry(apiKey);
		}, this);
	}

	private addEntry(apiKey: ApiKeyDto): void {
		let entry = {
			guid: apiKey.Guid,
			name: apiKey.Name,
			active: (apiKey.IsActive ? 'Yes' : 'No'),
			lastActivity: this.formatDateHm(apiKey.LastActivityTime),
			owner: apiKey.Owner,
			tag: apiKey
		};

		this.entries.push(entry);
	}

	private updateEntry(apiKey: ApiKeyDto): void {
		let found = this.entries.some((entry) => {
			if (entry.tag == apiKey) {
				entry.guid = apiKey.Guid;
				entry.name = apiKey.Name;
				entry.active = (apiKey.IsActive ? 'Yes' : 'No');
				entry.lastActivity = this.formatDateHm(apiKey.LastActivityTime);
				entry.owner = apiKey.Owner;
				return true;
			}
			else {
				return false;
			}
		});

		if (!found)
			console.error("Did not find API key entry to update");
	}

	private removeEntry(apiKey: ApiKeyDto): void {
		for (let i = 0; i < this.entries.length; ++i) {
			if (this.entries[i].tag == apiKey) {
				this.entries.splice(i, 1);
				break;
			}
		}
	}

	private triggerChangeDetection(): void {
		this.entries = [...this.entries];
	}

	public onApiKeyOkClicked(apiKey: ApiKeyDto): void {
		if (this.apiKeyDialogCreate) {
			// If new API key is being created, append entry for it
			this.addEntry(apiKey);
		}
		else {
			// If existing API key is being updated, refresh the existing
			// entry with the API key's updated properties
			this.updateEntry(apiKey);
		}

		// Force redisplay of modified entries
		this.triggerChangeDetection();
	}

	public onApiKeyCancelClicked(): void {
		// Do nothing
	}

	public onDeleteOkClicked(): void {
		this.displayDeleteApiKeyConfirm = false;

		let apiKeyToDelete = this.apiKeyToDelete;
		this.apiKeyToDelete = null;

		if (apiKeyToDelete) {
			this.apiKeysService.deleteApiKey(apiKeyToDelete.Guid).subscribe(
				(result: boolean) => {
					// Remove deleted entry
					this.removeEntry(apiKeyToDelete);

					// Force redisplay of modified entries
					this.triggerChangeDetection();
				},
				(error) => {
					this.messageService.displayErrorMessage("Deleting API key failed");
				});
		}
	}

	public onDeleteCancelClicked(): void {
		this.displayDeleteApiKeyConfirm = false;
		this.apiKeyToDelete = null;
	}
	
	public xferApiKey: ApiKeyDto;
	public xferUserSelectorExceptions: string[];
	public xferTargetUser: UserDto;

	public onTransferClick(event: any, apiKey: ApiKeyDto): void {
		event.stopPropagation();
		this.xferApiKey = apiKey;
		this.xferUserSelectorExceptions = [ apiKey.UserGuid ];
		this.xferTargetUser = null;
		this.displayUserSelectorDialog = true;
	}

	public doApiKeyTransfer(selectedUsers: UserDto[]): void {
		this.xferTargetUser = selectedUsers[0];

		this.apiKeysService.doApiKeyTransfer(this.xferApiKey.Guid, this.xferApiKey.UserGuid, 
												this.xferTargetUser.Guid).subscribe(
			(result: ApiKeyTransferResultDto) => {
				this.messageService.displayInfoMessage("The API key's owner has been changed.");

				this.xferApiKey.UserGuid = this.xferTargetUser.Guid;
				this.xferApiKey.Owner = this.xferTargetUser.Username;

				this.rebuildEntries(this.apiKeys);
				this.resetApiKeyTransfer();
			},
			(error) => {
				this.messageService.displayErrorMessage("The API key's owner could not be changed. Try again later.");
				this.resetApiKeyTransfer();
			});
	}

	public cancelApiKeyTransfer(): void {
		this.resetApiKeyTransfer();
	}

	private resetApiKeyTransfer(): void {
		this.xferApiKey = null;
		this.xferUserSelectorExceptions = [];
		this.xferTargetUser = null;
	}

	public doCustomSort(event): void {
		let comparer = function (a: any, b: any): number {
			let result = 0;

			switch (event.field) {
				case 'lastActivity':
					{
						let aDate = new Date(a.tag.LastActivityTime);
						let bDate = new Date(b.tag.LastActivityTime);
						result = ((aDate < bDate) ? -1 : ((aDate > bDate) ? 1 : 0));
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].localeCompare(b[event.field]);
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
